import { useState } from 'react';

import type { VoucherCancellationBulkBlock } from '@voll-intelligence/genui-contract';
import { withBold } from '../../../shared/ui/bold_markup.js';
import { CardFooter } from '../../../shared/ui/card.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { CheckIcon } from '@phosphor-icons/react';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';
import './voucher_cancellation_bulk.css';

/**
 * Card de cancelamento em LOTE (`voucher_cancellation_bulk`) — lista os vouchers
 * canceláveis com checkbox; o cliente marca os que quer cancelar e submete de uma
 * vez. O submit ecoa o `challengeId` ESTRUTURADO (guardrail server-side) + os
 * códigos marcados FORA do texto livre (`onSubmit` → `interaction.data` →
 * `confirmation.selections`); o backend re-avalia cada voucher antes de cancelar.
 * `postEmission` avisa que o voucher emitido vira uma SOLICITAÇÃO. `readOnly`
 * congela (zero seleção/CTA) — conversa encerrada ou submit já resolvido.
 */
export function VoucherCancellationBulkCard({
  ui,
  note,
  readOnly = false,
  onSubmit,
  onQuickReply,
}: {
  readonly ui: VoucherCancellationBulkBlock;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly onSubmit?: (voucherCodes: string[]) => void;
  readonly onQuickReply?: (text: string) => void;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Trava local anti duplo-clique (a trava real — single-use — é server-side).
  const [sent, setSent] = useState(false);
  const { t } = useGenUiI18n();

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const count = selected.size;
  const cancelSelectedLabel =
    count > 0 ? t('voucherCancelBulk.submitCount', { count }) : t('voucherCancelBulk.submit');
  const anyPostEmission = ui.vouchers.some((v) => v.postEmission === true);

  return (
    <section className="fui vcb" aria-label={t('voucherCancelBulk.aria')}>
      <div className="fui-card">
        <div className="fui-body">
          <p className="fui-cf-lead">{withBold(t('voucherCancelBulk.lead'))}</p>
          {anyPostEmission && <p className="vcb__note">{withBold(t('voucherCancelBulk.note'))}</p>}
          <ul className="vcb__list">
            {ui.vouchers.map((v) => {
              const checked = selected.has(v.voucherCode);
              return (
                <li className={`vcb__row${checked ? ' is-checked' : ''}`} key={v.voucherCode}>
                  <label className="vcb__label">
                    {!readOnly && (
                      <input
                        type="checkbox"
                        className="vcb__check"
                        checked={checked}
                        disabled={sent}
                        onChange={() => {
                          toggle(v.voucherCode);
                        }}
                      />
                    )}
                    <span className="vcb__info">
                      <span className="vcb__code is-mono">{v.voucherCode}</span>
                      <span className="vcb__meta">
                        {v.route && (
                          // "Trecho" só quando é rota (origem → destino); estadia → "Local".
                          <span className="vcb__tag">
                            {v.route.includes(' → ')
                              ? t('voucherCancel.k.route')
                              : t('voucherCancel.k.place')}
                            : {v.route}
                          </span>
                        )}
                        {v.modal && <span className="vcb__tag">{v.modal}</span>}
                        {v.status && <span className="vcb__tag">{v.status}</span>}
                        {v.postEmission && (
                          <span className="vcb__tag is-warn">{t('voucherCancelBulk.issued')}</span>
                        )}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        <CardFooter note={note}>
          {!readOnly && (
            <div className="fui-actions">
              <button
                type="button"
                className="fui-btn fui-btn-primary fui-btn-lg"
                disabled={sent || count === 0 || !onSubmit}
                onClick={() => {
                  setSent(true);
                  onSubmit?.([...selected]);
                }}
              >
                <CheckIcon size={14} aria-hidden />
                {sent ? t('voucherCancelBulk.sent') : cancelSelectedLabel}
              </button>
              {onQuickReply && !sent && (
                <button
                  type="button"
                  className="fui-btn fui-btn-outline fui-btn-lg"
                  onClick={() => {
                    onQuickReply(t('voucherCancelBulk.replyKeepAll'));
                  }}
                >
                  {t('voucherCancelBulk.keepAll')}
                </button>
              )}
            </div>
          )}
        </CardFooter>
      </div>
    </section>
  );
}
